import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Maximize2,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { useTheme } from "../../../hooks/useTheme.js";

// Tutorial player that must be watched end to end before the caller lets the
// candidate move on.
//
//  - autoplays on mount (falls back to a play overlay when the browser blocks it)
//  - no native controls, so there is no scrubber to drag
//  - any seek past the furthest point actually watched is rewound
//  - pause/resume, replay and fullscreen are the only controls
//  - a load failure or a long buffering stall calls onWatched(), so a broken
//    asset or a bad connection can never trap a candidate on this screen
const formatTime = (seconds) => {
  if (!Number.isFinite(seconds)) return "0:00";
  const total = Math.max(0, Math.floor(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

// Tolerance for the seek guard. `timeupdate` fires ~4x/sec at best and is
// throttled hard on loaded or low-power devices, and some browsers fire
const SEEK_TOLERANCE_SECONDS = 3;

// How long playback may sit buffering before we stop gating on the video.
const STALL_UNLOCK_MS = 20000;

const GatedTutorialVideo = ({ src, poster, watched, onWatched }) => {
  const { theme } = useTheme();
  const isLight = theme === "light";

  const videoRef = useRef(null);
  // Furthest point genuinely reached during playback — the seek ceiling.
  const maxWatchedRef = useRef(0);
  const stallTimerRef = useRef(null);

  const [needsManualStart, setNeedsManualStart] = useState(false);
  const [failed, setFailed] = useState(false);
  const [stalled, setStalled] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [hasEnded, setHasEnded] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const clearStallTimer = () => {
    if (stallTimerRef.current) {
      clearTimeout(stallTimerRef.current);
      stallTimerRef.current = null;
    }
  };

  useEffect(() => clearStallTimer, []);

  // Autoplay once, and only while there is still something to watch. Browsers
  // reject unmuted autoplay unless the user has interacted with the page, so a
  // rejection just surfaces the play overlay rather than muting a narrated clip.
  useEffect(() => {
    if (watched) return;
    const video = videoRef.current;
    if (!video) return;

    const attempt = video.play();
    if (attempt?.catch) {
      attempt
        .then(() => setNeedsManualStart(false))
        .catch(() => setNeedsManualStart(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recordProgress = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.currentTime > maxWatchedRef.current) {
      maxWatchedRef.current = video.currentTime;
    }
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;
    recordProgress();
    setCurrentTime(video.currentTime);
  };

  // Rewinding is fine; jumping ahead of what has been watched is not. This also
  // covers keyboard seeking and anything else that moves currentTime.
  const handleSeeking = () => {
    const video = videoRef.current;
    if (!video) return;
    if (watched) return; // already earned — replay can be scrubbed freely
    if (video.currentTime > maxWatchedRef.current + SEEK_TOLERANCE_SECONDS) {
      video.currentTime = maxWatchedRef.current;
    }
  };

  const handleEnded = () => {
    clearStallTimer();
    setNeedsManualStart(false);
    setIsPlaying(false);
    setIsBuffering(false);
    setHasEnded(true);
    onWatched();
  };

  const handleError = useCallback(() => {
    clearStallTimer();
    setFailed(true);
    setIsBuffering(false);
    // Never let a broken asset block the flow.
    onWatched();
  }, [onWatched]);

  // The buffer ran dry. Give it a window to recover; if it doesn't, stop
  // gating — a stalled download must not strand the candidate here, since
  // `ended` would otherwise never fire.
  const handleWaiting = () => {
    setIsBuffering(true);
    if (stallTimerRef.current) return;
    stallTimerRef.current = setTimeout(() => {
      stallTimerRef.current = null;
      setStalled(true);
      onWatched();
    }, STALL_UNLOCK_MS);
  };

  const handlePlaying = () => {
    recordProgress();
    clearStallTimer();
    setIsBuffering(false);
    setIsPlaying(true);
  };

  const startPlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    video.play().then(
      () => setNeedsManualStart(false),
      () => setNeedsManualStart(true)
    );
  };

  const replay = () => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    setCurrentTime(0);
    setHasEnded(false);
    startPlayback();
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      startPlayback();
    } else {
      video.pause();
    }
  };

  // Clicking the frame is the familiar way to pause; once finished it restarts.
  const handleSurfaceClick = () => {
    if (failed) return;
    if (hasEnded) {
      replay();
    } else {
      togglePlay();
    }
  };

  const enterFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;
    const request =
      video.requestFullscreen ||
      video.webkitRequestFullscreen ||
      video.webkitEnterFullscreen || // iOS Safari only exposes this one
      video.msRequestFullscreen;
    if (request) request.call(video);
  };

  const progress = duration > 0 ? Math.min(1, currentTime / duration) : 0;

  const controlButtonClass = `flex items-center gap-1.5 text-[12px] font-medium transition-colors ${
    isLight
      ? "text-slate-500 hover:text-slate-700"
      : "text-gray-300 hover:text-white"
  }`;

  return (
    <div>
      <div className="relative">
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          playsInline
          preload="auto"
          disablePictureInPicture
          // No `controls`: the native scrubber is the main way to skip ahead.
          onClick={handleSurfaceClick}
          onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
          onTimeUpdate={handleTimeUpdate}
          onSeeking={handleSeeking}
          onPlay={() => {
            setIsPlaying(true);
            setHasEnded(false);
          }}
          onPause={() => setIsPlaying(false)}
          onWaiting={handleWaiting}
          onPlaying={handlePlaying}
          onEnded={handleEnded}
          onError={handleError}
          className="w-full aspect-[16/9] rounded-lg bg-black object-contain cursor-pointer"
        />

        {needsManualStart && !failed && (
          <button
            type="button"
            onClick={startPlayback}
            aria-label="Play tutorial"
            className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/40 transition-colors hover:bg-black/50"
          >
            <span className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center">
              <Play
                className="w-6 h-6 text-slate-800 ml-0.5"
                strokeWidth={2}
                fill="currentColor"
              />
            </span>
          </button>
        )}

        {/* Without native controls there is no built-in spinner — a frozen
            frame would otherwise read as "broken". */}
        {isBuffering && !failed && !needsManualStart && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-lg bg-black/40 pointer-events-none">
            <Loader2 className="w-7 h-7 text-white animate-spin" />
            <span className="text-[12px] text-white/90">Loading…</span>
          </div>
        )}
      </div>

      {/* Progress is display only — there is no scrubber to drag. */}
      <div
        className={`mt-3 h-[3px] w-full rounded-full overflow-hidden ${
          isLight ? "bg-[#ECEDF5]" : "bg-gray-700"
        }`}
      >
        <div
          className="h-full rounded-full bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] transition-[width] duration-200"
          style={{ width: `${progress * 100}%` }}
        />
      </div>

      <div className="flex items-center justify-between gap-3 mt-2.5">
        <div className="min-w-0">
          {failed ? (
            <p className="flex items-center gap-1.5 text-[12px] text-amber-500">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
              Tutorial couldn't load.
            </p>
          ) : stalled ? (
            <p className="flex items-center gap-1.5 text-[12px] text-amber-500">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
              Slow connection
            </p>
          ) : null}
        </div>

        <div className="flex items-center gap-4 shrink-0">
          {duration > 0 && !failed && (
            <span
              className={`text-[11px] tabular-nums ${
                isLight ? "text-slate-400" : "text-gray-500"
              }`}
            >
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          )}

          {!failed && hasEnded && (
            <button type="button" onClick={replay} className={controlButtonClass}>
              <RotateCcw className="w-3.5 h-3.5" />
              Play again
            </button>
          )}

          {!failed && !hasEnded && (
            <button
              type="button"
              onClick={togglePlay}
              className={controlButtonClass}
            >
              {isPlaying ? (
                <>
                  <Pause className="w-3.5 h-3.5" />
                </>
              ) : (
                <>
                  <Play className="w-3.5 h-3.5" />
                </>
              )}
            </button>
          )}

          {!failed && (
            <button
              type="button"
              onClick={enterFullscreen}
              className={controlButtonClass}
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GatedTutorialVideo;
